
import Vue from 'vue';
import { Runnit, RunnitMember, RunnitsAccessLevel, RUNNITS_ACCESS_LEVEL } from '@run-diffusion/shared';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { db, Query } from '@/firebase';
import { mapActions, mapState } from 'vuex';
import RunnitBoardMemberChip from './RunnitBoardMemberChip.vue';

export default Vue.extend({
	name: 'RunnitBoardMembersTableCell',
	mixins: [
		TeamUserMixin,
	],
	props: {
		runnit: { type: Object as () => Runnit, required: true },
		// The board is gone (deleted since the list loaded): show its access, but open nothing.
		disabled: { type: Boolean, default: false },
	},
	data () {
		return {
			RUNNITS_ACCESS_LEVEL,

			runnitMembers: [] as RunnitMember[],
			runnitMembersLoading: false,
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		displayMembers (): RunnitMember[] {
			return this.runnitMembers.slice(0, 10);
		},
		isRunnitOwner (): boolean {
			return !!(
				this.user &&
				this.runnit?.userId &&
				this.runnit.userId === this.user.id
			);
		},
		canManageAccess (): boolean {
			return !!(
				(
					// Can switch it to PRIVATE without a permission if I'm the owner
					this.isRunnitOwner &&
					this.runnit?.accessLevel !== RUNNITS_ACCESS_LEVEL.PRIVATE
				) ||
				(
					(this.isRunnitOwner || this.hasManageAllGenerationsPermission) &&
					this.hasShareRunnitAssetsPermission
				)
			);
		},
		/** Access Settings also holds the owner, so whoever can change it can open them. */
		canOpenAccessSettings (): boolean {
			return !this.disabled && (this.canManageAccess || this.canChangeRunnitOwner(this.runnit));
		},
	},
	watch: {
		runnit: {
			immediate: true,
			handler (newVal: Runnit, oldVal: Runnit) {
				const newRunnitId: string = newVal?.id || null;
				const oldRunnitId: string = oldVal?.id || null;
				const newAccessLevel: RunnitsAccessLevel = newVal?.accessLevel || null;
				const oldAccessLevel: RunnitsAccessLevel = oldVal?.accessLevel || null;
				if (newRunnitId !== oldRunnitId || newAccessLevel !== oldAccessLevel) {
					// Only load members for RESTRICTED access level
					if (newAccessLevel === RUNNITS_ACCESS_LEVEL.RESTRICTED) {
						this.loadRunnitMembers(newRunnitId);
					} else {
						// Clear members for other access levels
						this.runnitMembers = [];
					}
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
		]),
		async loadRunnitMembers (runnitId: string) {
			this.runnitMembers = [];
			if (!runnitId) return;

			this.runnitMembersLoading = true;

			try {
				const runnitMembersRef: Query = db.collection(`runnits/${runnitId}/runnitMembers`)
					.orderBy('createdAt', 'asc')
					.limit(11);

				await this.$bind(
					'runnitMembers',
					runnitMembersRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error loading runnit members:', error);
			} finally {
				this.runnitMembersLoading = false;
			}
		},
		onManageAccessSettingsClick () {
			if (this.disabled) return;
			this.updateRunnitState({
				runnitManageAccessOpen: true,
				runnit: this.runnit,
			});
		},
	},
	components: {
		RunnitBoardMemberChip,
	},
});
